/**
 * Guards spreadsheet exports (CSV / XLSX) against formula injection.
 *
 * Passenger-controlled strings (names, notes, PNR, ...) that start with
 * = + - @ TAB or CR can be interpreted as formulas when staff open an export
 * in Excel / Sheets. Such strings are prefixed with a single quote so they
 * are treated as text.
 *
 * Only string values are touched. Numbers, dates, booleans, null/undefined and
 * objects (e.g. ExcelJS formula / rich-text values) are returned unchanged.
 * Plain numeric/time strings such as "-5", "+1.5", "-00:05", "-12%" and the
 * lone "-" placeholder are also left alone, as they cannot carry a formula.
 */

const FORMULA_TRIGGER = /^[=+\-@\t\r]/;
const NUMERIC_LIKE = /^[+-]?[\d.,: ]*\d[\d.,: ]*%?$/;

export const sanitizeSpreadsheetCell = (value) => {
    if (typeof value !== 'string') return value;
    if (!FORMULA_TRIGGER.test(value)) return value;
    if (value === '-' || NUMERIC_LIKE.test(value)) return value;
    return `'${value}`;
};

/** Sanitise every value of a row array. */
export const sanitizeSpreadsheetRow = (row) =>
    Array.isArray(row) ? row.map(sanitizeSpreadsheetCell) : row;

/** Sanitise every value of a row object (returns a new object). */
export const sanitizeSpreadsheetRecord = (record) => {
    if (!record || typeof record !== 'object' || Array.isArray(record)) return record;
    const out = {};
    Object.keys(record).forEach((k) => {
        out[k] = sanitizeSpreadsheetCell(record[k]);
    });
    return out;
};

/**
 * ExcelJS: sanitise all string cell values in a workbook in place.
 * Call immediately before workbook.xlsx.writeBuffer().
 */
export const sanitizeExcelWorkbook = (workbook) => {
    if (!workbook?.eachSheet) return workbook;
    workbook.eachSheet((sheet) => {
        sheet.eachRow({ includeEmpty: false }, (row) => {
            row.eachCell({ includeEmpty: false }, (cell) => {
                if (cell.isMerged && cell.master !== cell) return;
                if (typeof cell.value === 'string') {
                    const safe = sanitizeSpreadsheetCell(cell.value);
                    if (safe !== cell.value) cell.value = safe;
                }
            });
        });
    });
    return workbook;
};

/**
 * SheetJS: sanitise all string cells of a worksheet in place.
 * Formula cells are converted to (escaped) text: SheetJS's CSV reader turns
 * any "=..." value into a live formula, and none of our exports intend one.
 * Call after json_to_sheet / aoa_to_sheet / XLSX.read and before XLSX.write.
 */
export const sanitizeSheetJsWorksheet = (worksheet) => {
    if (!worksheet) return worksheet;
    Object.keys(worksheet).forEach((addr) => {
        if (addr[0] === '!') return;
        const cell = worksheet[addr];
        if (!cell) return;
        if (cell.f) {
            worksheet[addr] = { t: 's', v: sanitizeSpreadsheetCell(`=${cell.f}`) };
            return;
        }
        if (cell.t === 's' && typeof cell.v === 'string') {
            const safe = sanitizeSpreadsheetCell(cell.v);
            if (safe !== cell.v) {
                cell.v = safe;
                delete cell.w;
                delete cell.h;
                delete cell.r;
            }
        }
    });
    return worksheet;
};

/** SheetJS: sanitise every worksheet of a workbook in place. */
export const sanitizeSheetJsWorkbook = (workbook) => {
    if (!workbook?.Sheets) return workbook;
    Object.values(workbook.Sheets).forEach(sanitizeSheetJsWorksheet);
    return workbook;
};
